import { RowData, RowId } from '.';

export interface DisabledState {
  disabled: boolean;
  reason?: string;
}

/**
 * Shared interface for row customization props
 * 
 * @property {function} isRowSelectable - Optional callback to determine if a row can be selected via checkbox.
 *   - Receives the row data as parameter
 *   - Returns true if the row should be selectable, false otherwise
 *   - If not provided, all rows are selectable by default
 *   - Example: `(row) => row.status !== 'locked'`
 * 
 * @property {function} isRowDisabled - Optional callback to determine if a row should be disabled.
 *   - Receives the row data as parameter
 *   - Can return a boolean or an object with disabled state and optional reason
 *   - If boolean: true means row is disabled, false means enabled
 *   - If object: { disabled: boolean, reason?: string } - reason will be shown in tooltip
 *   - Disabled rows cannot be clicked or selected, and appear with a visual overlay
 *   - Example: `(row) => row.isPending` or `(row) => ({ disabled: row.isPending, reason: 'Action pending' })`
 * 
 * @property {function} getRowClassName - Optional callback to add custom CSS classes to a row.
 *   - Receives an object with the row data as parameter: { row: RowData }
 *   - Returns a string containing space-separated CSS class names
 *   - Classes are appended to the default row classes
 *   - Example: `({ row }) => row.priority === 'high' ? 'highlight-row' : ''`
   - Example: `(row) => !row.inSwitchingProgress`
 */
export interface RowCustomizationProps {
  isRowSelectable?: (row: RowData) => boolean;
  isRowDisabled?: (row: RowData) => boolean | { disabled: boolean; reason?: string };
  getRowClassName?: (params: any) => string;
}

/**
 * Checks if a row is selectable
 */
export const checkRowSelectable = (
  row: RowData,
  isRowSelectable?: (row: RowData) => boolean
): boolean => {
  return isRowSelectable ? isRowSelectable(row) : true;
};

/**
 * Gets the disabled state of a row
 */
export const getRowDisabledState = (
  row: RowData,
  isRowDisabled?: (row: RowData) => boolean | { disabled: boolean; reason?: string }
): DisabledState => {
  if (!isRowDisabled) {
    return { disabled: false, reason: undefined };
  }

  const result = isRowDisabled(row);

  if (typeof result === 'boolean') {
    return { disabled: result, reason: undefined };
  }

  return { disabled: result.disabled, reason: result.reason };
};

/**
 * Gets the custom class name for a row
 */
export const getCustomRowClassName = (
  row: RowData,
  getRowClassName?: (params: any) => string
): string => {
  return getRowClassName ? getRowClassName({ row }) : '';
};

/**
 * Filters rows based on selectability and disabled state
 */
export const getSelectableRows = (
  rows: RowData[],
  isRowSelectable?: (row: RowData) => boolean,
  isRowDisabled?: (row: RowData) => boolean | { disabled: boolean; reason?: string }
): RowData[] => {
  return rows.filter((row) => {
    const isSelectable = checkRowSelectable(row, isRowSelectable);
    const { disabled } = getRowDisabledState(row, isRowDisabled);
    return isSelectable && !disabled;
  });
};

/**
 * Gets the IDs of selectable rows
 */
export const getSelectableRowIds = (
  rows: RowData[],
  isRowSelectable?: (row: RowData) => boolean,
  isRowDisabled?: (row: RowData) => boolean | { disabled: boolean; reason?: string }
): RowId[] => {
  return getSelectableRows(rows, isRowSelectable, isRowDisabled).map((row) => row.id);
};
